import { useEffect, useMemo, useState } from 'react'
import { Bell, Shield } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useTelegram } from '@/hooks/useTelegram'
import { useStudioStore } from '@/store'
import { Card, Badge } from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { LoadingScreen } from '@/components/LoadingScreen'

interface NotificationAdminOverview {
  partner_reminders_active: boolean
  partner_reminder_time: string | null
  uk_reports_active: boolean
  total_partners: number
  subscribed_partners: number
  unsubscribed_partners: number
}

interface UnsubscribedPartner {
  telegram_id: number
  first_name: string | null
  last_name: string | null
  username: string | null
  unsubscribed_at: string | null
}

const PARTNER_REMINDER_CODE = 'partner_morning_reminder'
const UK_REPORT_CODES = [
  'uk_daily_summary',
  'uk_missing_revenue',
  'uk_marketing_stale',
  'uk_crm_discrepancy'
]

function formatName(firstName?: string | null, lastName?: string | null): string {
  return `${firstName || ''} ${lastName || ''}`.trim() || 'Без имени'
}

function formatTime(value: string | null): string {
  if (!value) return '09:00'
  return value.slice(0, 5)
}

export function NotificationAdminPage() {
  const { showAlert, hapticFeedback } = useTelegram()
  const { studios } = useStudioStore()
  const [loading, setLoading] = useState(true)
  const [savingPartnerToggle, setSavingPartnerToggle] = useState(false)
  const [savingUkToggle, setSavingUkToggle] = useState(false)
  const [overview, setOverview] = useState<NotificationAdminOverview | null>(null)
  const [unsubscribedPartners, setUnsubscribedPartners] = useState<UnsubscribedPartner[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  const isManagementCompany = useMemo(
    () => studios.some((studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'),
    [studios]
  )

  useEffect(() => {
    if (!isManagementCompany) {
      setLoading(false)
      return
    }
    void loadAll()
  }, [isManagementCompany])

  const effectiveOverview = useMemo<NotificationAdminOverview | null>(() => {
    if (overview) return overview
    return null
  }, [overview])

  const loadAll = async () => {
    setLoading(true)
    setLoadError(null)

    await Promise.all([loadOverview(), loadUnsubscribedPartners()])

    setLoading(false)
  }

  const loadOverview = async () => {
    const { data, error } = await (supabase as any).rpc('get_notification_admin_overview')

    if (!error && data) {
      const row = Array.isArray(data) ? data[0] : data
      setOverview(row as NotificationAdminOverview)
      return
    }

    const { data: typeRows, error: typeError } = await (supabase as any)
      .from('notification_types')
      .select('code,is_active')
      .in('code', [PARTNER_REMINDER_CODE, ...UK_REPORT_CODES])

    if (typeError) {
      console.error('Failed to load admin overview fallback:', typeError)
      setLoadError('Новые RPC админки недоступны. Завершите миграцию БД (Фаза 1).')
      setOverview(null)
      return
    }

    const rows = typeRows || []
    const partnerType = rows.find((row: any) => row.code === PARTNER_REMINDER_CODE)
    const ukTypes = rows.filter((row: any) => UK_REPORT_CODES.includes(row.code))
    const allUkActive = ukTypes.length > 0 && ukTypes.every((row: any) => Boolean(row.is_active))

    setOverview({
      partner_reminders_active: Boolean(partnerType?.is_active),
      partner_reminder_time: '09:00:00',
      uk_reports_active: allUkActive,
      total_partners: 0,
      subscribed_partners: 0,
      unsubscribed_partners: 0
    })
    setLoadError('Работаем в ограниченном fallback-режиме до миграции новых RPC.')
  }

  const loadUnsubscribedPartners = async () => {
    const { data, error } = await (supabase as any).rpc('get_unsubscribed_partners')
    if (!error && Array.isArray(data)) {
      setUnsubscribedPartners(data as UnsubscribedPartner[])
      return
    }

    setUnsubscribedPartners([])
  }

  const handleTogglePartnerReminders = async (nextValue: boolean) => {
    setSavingPartnerToggle(true)

    const { error: nextApiError } = await (supabase as any)
      .from('notification_types')
      .update({ is_active: nextValue })
      .eq('code', PARTNER_REMINDER_CODE)

    setSavingPartnerToggle(false)

    if (nextApiError) {
      console.error('Failed to toggle partner reminders:', nextApiError)
      hapticFeedback.notification('error')
      showAlert('Не удалось обновить статус напоминаний франчайзи')
      return
    }

    hapticFeedback.notification('success')
    await loadOverview()
  }

  const handleToggleUkReports = async (nextValue: boolean) => {
    setSavingUkToggle(true)

    const { error } = await (supabase as any)
      .from('notification_types')
      .update({ is_active: nextValue })
      .in('code', UK_REPORT_CODES)

    setSavingUkToggle(false)

    if (error) {
      console.error('Failed to toggle UK reports:', error)
      hapticFeedback.notification('error')
      showAlert('Не удалось обновить статус отчётов УК')
      return
    }

    hapticFeedback.notification('success')
    await loadOverview()
  }

  if (loading) return <LoadingScreen />

  if (!isManagementCompany) {
    return (
      <Card>
        <Badge variant="warning" text="Нет доступа" />
        <p className="text-sm text-muted-foreground mt-2">
          Страница доступна только сотрудникам УК.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold">Админка уведомлений</h1>
      </div>

      {loadError && (
        <Card>
          <Badge variant="warning" text="Частично доступно" />
          <p className="text-sm text-muted-foreground mt-2">{loadError}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => void loadAll()}>
            Обновить
          </Button>
        </Card>
      )}

      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Напоминания франчайзи</h2>
            <p className="text-sm text-muted-foreground">
              Глобальное управление ежедневными напоминаниями.
            </p>
            <p className="text-xs text-muted-foreground">
              Время отправки: {formatTime(effectiveOverview?.partner_reminder_time ?? null)}
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={Boolean(effectiveOverview?.partner_reminders_active)}
              disabled={savingPartnerToggle}
              onChange={(e) => void handleTogglePartnerReminders(e.target.checked)}
            />
            <span>{effectiveOverview?.partner_reminders_active ? 'Вкл' : 'Выкл'}</span>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">Всего франчайзи</p>
            <p className="text-2xl font-semibold">{effectiveOverview?.total_partners ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">Подписаны</p>
            <p className="text-2xl font-semibold">{effectiveOverview?.subscribed_partners ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">Отписаны</p>
            <p className="text-2xl font-semibold">{effectiveOverview?.unsubscribed_partners ?? 0}</p>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold">Отчёты УК</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Управляются как единая группа из 4 отчётов.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={Boolean(effectiveOverview?.uk_reports_active)}
              disabled={savingUkToggle}
              onChange={(e) => void handleToggleUkReports(e.target.checked)}
            />
            <span>{effectiveOverview?.uk_reports_active ? 'Вкл' : 'Выкл'}</span>
          </label>
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold mb-3">Отписавшиеся франчайзи</h2>

        {unsubscribedPartners.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Список пуст или недоступен до завершения миграции RPC `get_unsubscribed_partners`.
          </p>
        ) : (
          <div className="space-y-2">
            {unsubscribedPartners.map((row) => (
              <div
                key={row.telegram_id}
                className="rounded-lg border border-border p-3 flex items-center justify-between gap-3 flex-wrap"
              >
                <div>
                  <p className="font-medium">{formatName(row.first_name, row.last_name)}</p>
                  <p className="text-xs text-muted-foreground">
                    @{row.username || 'no_username'} · ID: {row.telegram_id}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground whitespace-nowrap">
                  {row.unsubscribed_at ? new Date(row.unsubscribed_at).toLocaleString() : 'Дата неизвестна'}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
